import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ExtractedColor } from "../engine/colorExtractor";
import type { ImportedSvgIcon } from "../engine/svgImporter";

export type Page = "start" | "onboarding" | "templates" | "generated" | "editor" | "gif-maker" | "qr-maker" | "threed" | "poster-tiler";

export type Layout = "icon-top" | "icon-left" | "icon-right" | "text-only" | "icon-only";
/**
 * Logo "frame" — the backdrop shape that wraps the entire composition
 * (icon + container + text). Sits between a transparent canvas and the content,
 * so brand-kit exports get a transparent edge with the framed logo in the centre.
 */
export type LogoFrame =
  | "none"
  | "circle"             // round (1:1)
  | "square"             // square, sharp corners
  | "rounded"            // square, mildly rounded corners
  | "pill"               // wide horizontal, fully rounded ends (2:1)
  | "rectangle"          // wide horizontal, sharp corners
  | "rectangle-rounded"; // wide horizontal, mildly rounded corners

export type LogoFrameBorderStyle = "solid" | "dashed" | "dotted";

export type ContainerGradient = "none" | "linear" | "radial";

export type ContainerShape =
  | "none" | "circle" | "square" | "rounded"
  // Object/scene shapes — wrap the icon in something thematic.
  | "glasses" | "phone" | "cupcake" | "pizza" | "computer"
  | "bubble"  | "cloud" | "house"   | "banner" | "tag"
  | "cup"     | "lightbulb"
  // Comic-book containers
  | "burst" | "speech-bubble" | "sticker";
export type IconPattern =
  | "none"               // solid fill (default)
  // Stripes
  | "stripes-h"          // horizontal stripes (AT&T globe style)
  | "stripes-v"          // vertical stripes
  | "stripes-diag"       // 45° diagonal stripes
  | "stripes-thin"       // pinstripes — premium, business-like
  | "stripes-thick"      // bold stripes — youthful, attention-grabbing
  | "stripes-double"     // double-line bands — clean and polished
  | "stripes-broken"     // dashed stripes — modern, tech
  | "stripes-wavy"       // wavy lines — relaxed, optical/lens-like
  // Dots
  | "dots"               // perfect dot grid
  | "dots-tiny"          // subtle dot texture
  | "dots-large"         // large dots — comic/fun
  | "dots-random"        // random scattered dots
  | "bubble-dots"        // varied dot sizes — kid-friendly
  | "halftone-fade"      // dots that grow top→bottom
  // Grids
  | "grid"               // simple square grid
  | "grid-micro"         // very fine grid
  | "grid-blueprint"     // engineering blueprint feel
  | "checkerboard"       // racing/retro
  | "checkerboard-tiny"  // subtle sporty texture
  // Geometric
  | "honeycomb"          // hexagonal grid — science/tech
  | "crosshatch"         // sketchy hand-drawn / engraved
  | "carbon-fiber"       // sporty premium weave
  // Waves
  | "waves"              // smooth flowing wave lines
  | "sound-waves"        // equalizer-style bars
  | "heartbeat"          // ECG line — medical
  | "topographic"        // map contour lines
  // Specialty
  | "circuit"            // electrical traces with pads
  | "starburst"          // rays from center — promo, energy
  | "concentric-rings"   // optical/lens/target
  | "bokeh"              // soft overlapping circles — camera/lens
  | "confetti"           // celebration tilted rectangles
  | "speed-lines";       // diagonal motion streaks — comic action
export type TitleStyle3D =
  | "flat"
  | "stacked"
  | "chrome"
  | "extruded"
  | "gold"
  | "neon"
  | "outline"
  | "glitch"
  | "comic";

export type ColorCategory = "blue" | "purple" | "pink" | "red" | "orange" | "yellow" | "green" | "teal" | "greyscale";

export interface OnboardingPrefs {
  industry: string | null;
  symbolTypes: string[];           // tags chosen on the "pick symbol types" step
  likedInspirations: string[];     // ids of liked sample logos
  colorPreferences: ColorCategory[];
}

/** A user-uploaded SVG icon (kept in localStorage with the rest of the draft). */
export interface CustomIcon {
  id: string;
  name: string;
  paths: string[];
  /** ViewBox so we can normalise into 0..100. */
  vb: { x: number; y: number; w: number; h: number };
  addedAt: string;
}

/** Snapshot used for undo/redo (only the visual style fields). */
export interface StyleSnapshot {
  title: string;
  slogan: string;
  iconId: string | null;
  qrText: string | null;
  titleFont: string;
  sloganFont: string;
  titleColor: string;
  sloganColor: string;
  iconColor: string;
  iconAccent: string;
  background: string;
  layout: Layout;
  titleSize: number;
  sloganSize: number;
  iconScale: number;
}

/**
 * A full-fidelity snapshot saved as a "premade design" — appears as a
 * suggestion in the Templates page so the next session (or anyone using
 * this LOGOMAN PRO instance) can pick it as a starting point. Captures
 * every style field including frame, border, container, patterns, and 3D.
 */
export interface MyTemplate {
  id: string;
  name: string;
  savedAt: string;
  title: string;
  slogan: string;
  iconId: string | null;
  titleFont: string;
  sloganFont: string;
  titleColor: string;
  sloganColor: string;
  iconColor: string;
  iconAccent: string;
  background: string;
  layout: Layout;
  titleSize: number;
  sloganSize: number;
  iconScale: number;
  logoFrame: LogoFrame;
  logoFrameColor: string;
  logoFrameWidth: number;
  logoFrameHeight: number;
  logoFrameBorderColor: string;
  logoFrameBorderWidth: number;
  logoFrameBorderStyle: LogoFrameBorderStyle;
  containerShape: ContainerShape;
  containerColor: string;
  containerColorOpacity: number;
  containerGradient: ContainerGradient;
  containerColor2: string;
  containerGradientAngle: number;
  containerPattern: IconPattern;
  containerPatternColor: string;
  containerPatternDensity: number;
  iconPattern: IconPattern;
  iconPatternColor: string;
  iconPatternDensity: number;
  titleStyle3D: TitleStyle3D;
  titleDepth3D: number;
  titleStroke3D: number;
  titleSkew: number;
  sloganStyle3D: TitleStyle3D;
  sloganDepth3D: number;
  sloganStroke3D: number;
  sloganSkew: number;
  titleSloganGap: number;
  sloganOffsetX: number;
  iconTextGap: number;
  titleOffsetX: number;
  titleOffsetY: number;
  /**
   * Whether this design is shared in the public community gallery on the
   * home page. Defaults to "private" for safety. Older saves that pre-date
   * this field load as private (handled by the rehydration migration).
   */
  visibility?: "public" | "private";
  /** Firestore doc id when published as public — lets the user un-publish later. */
  firestoreDocId?: string;
}

/**
 * A snapshot saved to the user's personal library.
 *
 * Unified with MyTemplate so the 💾 Save button captures every visual field
 * (frame, border, container, gradients, patterns, 3D text, spacing, position
 * offsets) — previously SavedLogo only had ~15 fields and silently dropped
 * the rest, so saves looked nothing like the design after reload.
 */
export type SavedLogo = MyTemplate;

export interface LogoState {
  // Flow
  currentPage: Page;
  setPage: (p: Page) => void;

  // Content
  title: string;
  slogan: string;
  setTitle: (t: string) => void;
  setSlogan: (s: string) => void;

  // Icon
  iconId: string | null;
  setIcon: (id: string | null) => void;

  // Custom user-uploaded SVG icons
  customIcons: CustomIcon[];
  addCustomIcon: (icon: CustomIcon) => void;
  removeCustomIcon: (id: string) => void;

  // QR code mode (when iconId === "__qr__", we render a QR using qrText)
  qrText: string | null;
  setQrText: (text: string | null) => void;

  // Undo / redo
  history: { past: StyleSnapshot[]; future: StyleSnapshot[] };
  pushHistory: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // Reference image + extracted palette (drives template + editor color suggestions)
  referencePalette: ExtractedColor[];
  referenceImageURL: string | null; // not persisted — re-upload if reload
  setReference: (palette: ExtractedColor[], url: string | null) => void;
  clearReference: () => void;

  // Style
  titleFont: string;
  sloganFont: string;
  titleColor: string;
  sloganColor: string;
  iconColor: string;
  iconAccent: string;
  background: string;
  layout: Layout;
  titleSize: number;
  sloganSize: number;
  iconScale: number;
  // Logo frame (a backdrop shape behind the entire composition; canvas stays transparent)
  logoFrame: LogoFrame;
  logoFrameColor: string;
  logoFrameSize: number;       // legacy — uniform fraction of canvas (still persisted for older drafts)
  logoFrameWidth: number;      // 0.2..1.0 — fraction of canvas WIDTH the frame fills
  logoFrameHeight: number;     // 0.2..1.0 — fraction of canvas HEIGHT the frame fills
  logoFrameBorderColor: string;
  logoFrameBorderWidth: number;       // 0..30 — 0 disables the border
  logoFrameBorderStyle: LogoFrameBorderStyle;
  containerShape: ContainerShape;
  containerColor: string;
  containerColorOpacity: number;       // 0..100 — alpha applied to the container fill
  containerGradient: ContainerGradient; // "none" | "linear" | "radial"
  containerColor2: string;             // second stop colour for gradient
  containerGradientAngle: number;      // 0..360 — angle (deg) for linear gradients
  containerPattern: IconPattern;       // reuse same pattern types for the container
  containerPatternColor: string;
  containerPatternDensity: number;     // 0.5..2 multiplier — denser/sparser pattern
  iconPattern: IconPattern;
  iconPatternColor: string; // color of stripes/dots — default tracks background for an AT&T-style cutout
  iconPatternDensity: number;
  titleStyle3D: TitleStyle3D;
  titleDepth3D: number;   // 1..30, used by stacked + extruded
  titleStroke3D: number;  // 0..20, outline width used by chrome / gold / extruded / outline
  titleSkew: number;      // -20..0, italic-style horizontal skew applied to the title
  // Slogan gets its own independent 3D treatment — mirror of the title fields.
  sloganStyle3D: TitleStyle3D;
  sloganDepth3D: number;
  sloganStroke3D: number;
  sloganSkew: number;
  // Spacing system — nudge the slogan relative to the title without
  // re-doing the layout. Default 12 matches the previous hardcoded gap.
  titleSloganGap: number;     // -50..200 — vertical distance between title and slogan
  sloganOffsetX: number;      // -200..200 — horizontal nudge for the slogan
  iconTextGap: number;        // -50..300 — distance between the icon and the title block
  titleOffsetX: number;       // -200..200 — horizontal nudge for the title
  titleOffsetY: number;       // -200..200 — vertical nudge for the title
  setStyle: (patch: Partial<Pick<LogoState,
    "titleFont" | "sloganFont" | "titleColor" | "sloganColor" |
    "iconColor" | "iconAccent" | "background" | "layout" | "titleSize" | "sloganSize" | "iconScale" |
    "logoFrame" | "logoFrameColor" | "logoFrameSize" |
    "logoFrameWidth" | "logoFrameHeight" |
    "logoFrameBorderColor" | "logoFrameBorderWidth" | "logoFrameBorderStyle" |
    "containerShape" | "containerColor" |
    "containerColorOpacity" | "containerGradient" | "containerColor2" | "containerGradientAngle" |
    "containerPattern" | "containerPatternColor" | "containerPatternDensity" |
    "iconPattern" | "iconPatternColor" | "iconPatternDensity" |
    "titleStyle3D" | "titleDepth3D" | "titleStroke3D" | "titleSkew" |
    "sloganStyle3D" | "sloganDepth3D" | "sloganStroke3D" | "sloganSkew" |
    "titleSloganGap" | "sloganOffsetX" | "iconTextGap" |
    "titleOffsetX" | "titleOffsetY">>) => void;

  // Onboarding preferences (Looka-style wizard)
  onboarding: OnboardingPrefs;
  setIndustry: (industry: string | null) => void;
  toggleSymbolType: (tag: string) => void;
  toggleInspiration: (id: string) => void;
  toggleColorPreference: (c: ColorCategory) => void;
  resetOnboarding: () => void;

  // My designs — user-saved templates that appear as suggestions on the Templates page
  myTemplates: MyTemplate[];
  saveCurrentAsTemplate: (name?: string, visibility?: "public" | "private") => string; // returns new id
  /** Attach a Firestore doc id to a local template after a public publish. */
  attachFirestoreDocId: (templateId: string, docId: string) => void;
  loadMyTemplate: (id: string) => void;
  deleteMyTemplate: (id: string) => void;
  renameMyTemplate: (id: string, name: string) => void;

  // Library
  savedLogos: SavedLogo[];
  saveCurrentToLibrary: (name?: string) => string; // returns new id
  loadFromLibrary: (id: string) => void;
  deleteFromLibrary: (id: string) => void;
  renameInLibrary: (id: string, name: string) => void;
  duplicateInLibrary: (id: string) => string;

  // Reset
  reset: () => void;
}

const DEFAULTS = {
  title: "Your Brand",
  slogan: "Your tagline here",
  iconId: null as string | null,
  titleFont: "Inter",
  sloganFont: "Inter",
  titleColor: "#0c2572",
  sloganColor: "#475569",
  iconColor: "#0c2572",
  iconAccent: "#ff8aa3",   // soft kawaii pink — used by multi-color face icons
  background: "transparent",
  layout: "icon-top" as Layout,
  titleSize: 56,
  sloganSize: 18,
  iconScale: 1,
  logoFrame: "none" as LogoFrame,
  logoFrameColor: "#ffffff",
  logoFrameSize: 0.85,
  logoFrameWidth: 0.85,
  logoFrameHeight: 0.85,
  logoFrameBorderColor: "#0c2572",
  logoFrameBorderWidth: 0,
  logoFrameBorderStyle: "solid" as LogoFrameBorderStyle,
  containerShape: "none" as ContainerShape,
  containerColor: "#e0e7ff",
  containerColorOpacity: 100,
  containerGradient: "none" as ContainerGradient,
  containerColor2: "#a5b4fc",
  containerGradientAngle: 135,
  containerPattern: "none" as IconPattern,
  containerPatternColor: "#ffffff",
  containerPatternDensity: 1,
  iconPattern: "none" as IconPattern,
  iconPatternColor: "#ffffff",
  iconPatternDensity: 1,
  titleStyle3D: "flat" as TitleStyle3D,
  titleDepth3D: 8,
  titleStroke3D: 0,
  titleSkew: 0,
  sloganStyle3D: "flat" as TitleStyle3D,
  sloganDepth3D: 4,
  sloganStroke3D: 0,
  sloganSkew: 0,
  titleSloganGap: 12,
  sloganOffsetX: 0,
  iconTextGap: 24,
  titleOffsetX: 0,
  titleOffsetY: 0,
};

export const useLogoStore = create<LogoState>()(
  persist(
    (set, get) => ({
      currentPage: "start",
      setPage: (p) => set({ currentPage: p }),

      title: DEFAULTS.title,
      slogan: DEFAULTS.slogan,
      setTitle: (title) => set({ title }),
      setSlogan: (slogan) => set({ slogan }),

      iconId: DEFAULTS.iconId,
      setIcon: (iconId) => set({ iconId }),

      customIcons: [],
      addCustomIcon: (icon) => set({ customIcons: [icon, ...get().customIcons] }),
      removeCustomIcon: (id) => set({ customIcons: get().customIcons.filter((x) => x.id !== id) }),

      qrText: null,
      setQrText: (qrText) => set({ qrText }),

      history: { past: [], future: [] },
      pushHistory: () => {
        const s = get();
        const snap = snapshotOf(s);
        const past = s.history.past.slice(-49); // cap to last 50 states
        set({ history: { past: [...past, snap], future: [] } });
      },
      undo: () => {
        const s = get();
        if (s.history.past.length === 0) return;
        const prev = s.history.past[s.history.past.length - 1];
        const newPast = s.history.past.slice(0, -1);
        const cur = snapshotOf(s);
        applySnapshot(set, prev);
        set({ history: { past: newPast, future: [cur, ...s.history.future] } });
      },
      redo: () => {
        const s = get();
        if (s.history.future.length === 0) return;
        const next = s.history.future[0];
        const newFuture = s.history.future.slice(1);
        const cur = snapshotOf(s);
        applySnapshot(set, next);
        set({ history: { past: [...s.history.past, cur], future: newFuture } });
      },
      canUndo: () => get().history.past.length > 0,
      canRedo: () => get().history.future.length > 0,

      referencePalette: [],
      referenceImageURL: null,
      setReference: (referencePalette, referenceImageURL) => set({ referencePalette, referenceImageURL }),
      clearReference: () => set({ referencePalette: [], referenceImageURL: null }),

      titleFont: DEFAULTS.titleFont,
      sloganFont: DEFAULTS.sloganFont,
      titleColor: DEFAULTS.titleColor,
      sloganColor: DEFAULTS.sloganColor,
      iconColor: DEFAULTS.iconColor,
      iconAccent: DEFAULTS.iconAccent,
      background: DEFAULTS.background,
      layout: DEFAULTS.layout,
      titleSize: DEFAULTS.titleSize,
      sloganSize: DEFAULTS.sloganSize,
      iconScale: DEFAULTS.iconScale,
      logoFrame: DEFAULTS.logoFrame,
      logoFrameColor: DEFAULTS.logoFrameColor,
      logoFrameSize: DEFAULTS.logoFrameSize,
      logoFrameWidth: DEFAULTS.logoFrameWidth,
      logoFrameHeight: DEFAULTS.logoFrameHeight,
      logoFrameBorderColor: DEFAULTS.logoFrameBorderColor,
      logoFrameBorderWidth: DEFAULTS.logoFrameBorderWidth,
      logoFrameBorderStyle: DEFAULTS.logoFrameBorderStyle,
      containerShape: DEFAULTS.containerShape,
      containerColor: DEFAULTS.containerColor,
      containerColorOpacity: DEFAULTS.containerColorOpacity,
      containerGradient: DEFAULTS.containerGradient,
      containerColor2: DEFAULTS.containerColor2,
      containerGradientAngle: DEFAULTS.containerGradientAngle,
      containerPattern: DEFAULTS.containerPattern,
      containerPatternColor: DEFAULTS.containerPatternColor,
      containerPatternDensity: DEFAULTS.containerPatternDensity,
      iconPattern: DEFAULTS.iconPattern,
      iconPatternColor: DEFAULTS.iconPatternColor,
      iconPatternDensity: DEFAULTS.iconPatternDensity,
      titleStyle3D: DEFAULTS.titleStyle3D,
      titleDepth3D: DEFAULTS.titleDepth3D,
      titleStroke3D: DEFAULTS.titleStroke3D,
      titleSkew: DEFAULTS.titleSkew,
      sloganStyle3D: DEFAULTS.sloganStyle3D,
      sloganDepth3D: DEFAULTS.sloganDepth3D,
      sloganStroke3D: DEFAULTS.sloganStroke3D,
      sloganSkew: DEFAULTS.sloganSkew,
      titleSloganGap: DEFAULTS.titleSloganGap,
      sloganOffsetX: DEFAULTS.sloganOffsetX,
      iconTextGap: DEFAULTS.iconTextGap,
      titleOffsetX: DEFAULTS.titleOffsetX,
      titleOffsetY: DEFAULTS.titleOffsetY,
      setStyle: (patch) => set(patch),

      onboarding: { industry: null, symbolTypes: [], likedInspirations: [], colorPreferences: [] },
      setIndustry: (industry) => set({ onboarding: { ...safeOnboarding(get().onboarding), industry } }),
      toggleSymbolType: (tag) => {
        const cur = safeOnboarding(get().onboarding);
        const list = cur.symbolTypes;
        const next = list.includes(tag) ? list.filter((x) => x !== tag) : [...list, tag];
        set({ onboarding: { ...cur, symbolTypes: next } });
      },
      toggleInspiration: (id) => {
        const cur = safeOnboarding(get().onboarding);
        const list = cur.likedInspirations;
        const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
        set({ onboarding: { ...cur, likedInspirations: next } });
      },
      toggleColorPreference: (c) => {
        const cur = safeOnboarding(get().onboarding);
        const list = cur.colorPreferences;
        const next = list.includes(c) ? list.filter((x) => x !== c) : [...list, c];
        set({ onboarding: { ...cur, colorPreferences: next } });
      },
      resetOnboarding: () => set({ onboarding: { industry: null, symbolTypes: [], likedInspirations: [], colorPreferences: [] } }),

      myTemplates: [],
      saveCurrentAsTemplate: (name?: string, visibility: "public" | "private" = "private") => {
        const id = "tpl_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
        const s = get();
        const tpl: MyTemplate = {
          ...buildTemplateSnapshot(s, id, (name && name.trim()) || s.title || "My design"),
          visibility,
        };
        set({ myTemplates: [tpl, ...s.myTemplates] });
        return id;
      },
      attachFirestoreDocId: (templateId, docId) => set({
        myTemplates: get().myTemplates.map((x) =>
          x.id === templateId ? { ...x, firestoreDocId: docId } : x
        ),
      }),
      loadMyTemplate: (id) => {
        const s = get();
        const t = s.myTemplates.find((x) => x.id === id);
        if (!t) return;
        s.pushHistory();
        applyTemplate(set, t);
      },
      deleteMyTemplate: (id) => set({ myTemplates: get().myTemplates.filter((x) => x.id !== id) }),
      renameMyTemplate: (id, name) => set({
        myTemplates: get().myTemplates.map((x) => x.id === id ? { ...x, name: name.trim() || x.name } : x),
      }),

      savedLogos: [],
      saveCurrentToLibrary: (name?: string) => {
        // Reuse the same full-snapshot logic as saveCurrentAsTemplate so the
        // library and templates are byte-identical in shape — no field drift.
        const id = "logo_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
        const s = get();
        const snapshot: SavedLogo = {
          ...buildTemplateSnapshot(s, id, (name && name.trim()) || s.title || "Untitled logo"),
        };
        set({ savedLogos: [snapshot, ...s.savedLogos] });
        return id;
      },
      loadFromLibrary: (id) => {
        // pushHistory() before applying so the user can undo a load if they
        // clicked the wrong design. Same protection for loadMyTemplate.
        const s = get();
        const found = s.savedLogos.find((x) => x.id === id);
        if (!found) return;
        s.pushHistory();
        applyTemplate(set, found);
      },
      deleteFromLibrary: (id) => set({ savedLogos: get().savedLogos.filter((x) => x.id !== id) }),
      renameInLibrary: (id, name) => set({
        savedLogos: get().savedLogos.map((x) => x.id === id ? { ...x, name: name.trim() || x.name } : x),
      }),
      duplicateInLibrary: (id) => {
        const orig = get().savedLogos.find((x) => x.id === id);
        if (!orig) return "";
        const newId = "logo_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
        const copy: SavedLogo = { ...orig, id: newId, name: orig.name + " (copy)", savedAt: new Date().toISOString() };
        set({ savedLogos: [copy, ...get().savedLogos] });
        return newId;
      },

      reset: () => set({ ...DEFAULTS, currentPage: "start", referencePalette: [], referenceImageURL: null }),
    }),
    {
      name: "logoman-pro-draft",
      version: 3, // bump when shape changes — invalidates stale persisted state
      // When upgrading from older versions, keep the user's library + saved
      // designs but reset everything else to defaults. Avoids field-shape
      // drift breaking renderLogoSVG / generateIdeas at runtime.
      migrate: (persistedRaw, fromVersion) => {
        const p = (persistedRaw && typeof persistedRaw === "object" ? persistedRaw : {}) as Partial<LogoState>;
        if (fromVersion < 3) {
          // Strip everything except the user's collections — merge() will
          // fill in defaults for every other field, avoiding shape drift.
          return {
            savedLogos: Array.isArray(p.savedLogos) ? p.savedLogos : [],
            myTemplates: Array.isArray(p.myTemplates) ? p.myTemplates : [],
            customIcons: Array.isArray(p.customIcons) ? p.customIcons : [],
          } as unknown as LogoState;
        }
        return p as unknown as LogoState;
      },
      // Drop transient/runtime-only fields. `currentPage` is excluded so the
      // app always opens on the home screen, even after a reload.
      partialize: (state) => {
        const { referenceImageURL: _omit, history: _h, currentPage: _p, ...rest } = state;
        void _omit; void _h; void _p;
        return rest;
      },
      // Custom merge so new fields added to the store are not undefined when
      // hydrating from older persisted state. Deep-merges nested objects.
      merge: (persistedRaw, current) => {
        const persisted = (persistedRaw && typeof persistedRaw === "object" ? persistedRaw : {}) as Partial<LogoState>;
        return {
          ...current,
          ...persisted,
          // Force-default new/nested fields so they're never undefined
          onboarding: {
            industry: null,
            symbolTypes: [],
            likedInspirations: [],
            colorPreferences: [],
            ...(persisted.onboarding || {}),
          },
          customIcons: Array.isArray(persisted.customIcons) ? persisted.customIcons : [],
          savedLogos: Array.isArray(persisted.savedLogos) ? persisted.savedLogos : [],
          myTemplates: Array.isArray(persisted.myTemplates) ? persisted.myTemplates : [],
          referencePalette: Array.isArray(persisted.referencePalette) ? persisted.referencePalette : [],
          // Migrate legacy single-axis logoFrameSize → independent width/height.
          // If user had a custom uniform size, copy it into both axes so their
          // draft visually matches what they last saved.
          logoFrameWidth:
            typeof persisted.logoFrameWidth === "number" ? persisted.logoFrameWidth
            : typeof persisted.logoFrameSize === "number" ? persisted.logoFrameSize
            : 0.85,
          logoFrameHeight:
            typeof persisted.logoFrameHeight === "number" ? persisted.logoFrameHeight
            : typeof persisted.logoFrameSize === "number" ? persisted.logoFrameSize
            : 0.85,
          containerShape: persisted.containerShape ?? "none",
          containerColor: persisted.containerColor ?? "#e0e7ff",
          titleStyle3D: persisted.titleStyle3D ?? "flat",
          titleDepth3D: typeof persisted.titleDepth3D === "number" ? persisted.titleDepth3D : 8,
          // Force-clear the legacy default of 6 — outlines are now opt-in via the slider.
          titleStroke3D: 0,
          sloganStroke3D: 0,
          titleSkew: typeof persisted.titleSkew === "number" ? persisted.titleSkew : 0,
          qrText: persisted.qrText ?? null,
          // Always reset transient fields that were never persisted
          referenceImageURL: null,
          history: { past: [], future: [] },
          currentPage: "start",
        };
      },
    }
  )
);

// Defensive normalizer for the onboarding object — guarantees no field is undefined.
function safeOnboarding(o: Partial<OnboardingPrefs> | undefined | null): OnboardingPrefs {
  return {
    industry: o?.industry ?? null,
    symbolTypes: Array.isArray(o?.symbolTypes) ? o!.symbolTypes : [],
    likedInspirations: Array.isArray(o?.likedInspirations) ? o!.likedInspirations : [],
    colorPreferences: Array.isArray(o?.colorPreferences) ? o!.colorPreferences : [],
  };
}

// ===== Snapshot helpers shared by Save-as-template AND Save-to-library =====
// Single source of truth for "what does a saved design include?" — adding a new
// visual field means adding it here ONCE, and both surfaces stay in lockstep.
// Exported so tests can verify round-trip correctness in isolation.
export function buildTemplateSnapshot(s: LogoState, id: string, name: string): MyTemplate {
  return {
    id, name,
    savedAt: new Date().toISOString(),
    title: s.title, slogan: s.slogan, iconId: s.iconId,
    titleFont: s.titleFont, sloganFont: s.sloganFont,
    titleColor: s.titleColor, sloganColor: s.sloganColor,
    iconColor: s.iconColor, iconAccent: s.iconAccent,
    background: s.background, layout: s.layout,
    titleSize: s.titleSize, sloganSize: s.sloganSize, iconScale: s.iconScale,
    logoFrame: s.logoFrame, logoFrameColor: s.logoFrameColor,
    logoFrameWidth: s.logoFrameWidth, logoFrameHeight: s.logoFrameHeight,
    logoFrameBorderColor: s.logoFrameBorderColor,
    logoFrameBorderWidth: s.logoFrameBorderWidth,
    logoFrameBorderStyle: s.logoFrameBorderStyle,
    containerShape: s.containerShape, containerColor: s.containerColor,
    containerColorOpacity: s.containerColorOpacity,
    containerGradient: s.containerGradient,
    containerColor2: s.containerColor2,
    containerGradientAngle: s.containerGradientAngle,
    containerPattern: s.containerPattern, containerPatternColor: s.containerPatternColor,
    containerPatternDensity: s.containerPatternDensity,
    iconPattern: s.iconPattern, iconPatternColor: s.iconPatternColor,
    iconPatternDensity: s.iconPatternDensity,
    titleStyle3D: s.titleStyle3D, titleDepth3D: s.titleDepth3D,
    titleStroke3D: s.titleStroke3D, titleSkew: s.titleSkew,
    sloganStyle3D: s.sloganStyle3D, sloganDepth3D: s.sloganDepth3D,
    sloganStroke3D: s.sloganStroke3D, sloganSkew: s.sloganSkew,
    titleSloganGap: s.titleSloganGap, sloganOffsetX: s.sloganOffsetX,
    iconTextGap: s.iconTextGap,
    titleOffsetX: s.titleOffsetX, titleOffsetY: s.titleOffsetY,
  };
}

export function applyTemplate(set: (patch: Partial<LogoState>) => void, t: MyTemplate) {
  set({
    title: t.title, slogan: t.slogan, iconId: t.iconId,
    titleFont: t.titleFont, sloganFont: t.sloganFont,
    titleColor: t.titleColor, sloganColor: t.sloganColor,
    iconColor: t.iconColor,
    iconAccent: t.iconAccent ?? "#ff8aa3",
    background: t.background, layout: t.layout,
    titleSize: t.titleSize, sloganSize: t.sloganSize, iconScale: t.iconScale,
    logoFrame: t.logoFrame ?? "none",
    logoFrameColor: t.logoFrameColor ?? "#ffffff",
    logoFrameWidth: t.logoFrameWidth ?? 0.85,
    logoFrameHeight: t.logoFrameHeight ?? 0.85,
    logoFrameBorderColor: t.logoFrameBorderColor ?? "#0c2572",
    logoFrameBorderWidth: t.logoFrameBorderWidth ?? 0,
    logoFrameBorderStyle: t.logoFrameBorderStyle ?? "solid",
    containerShape: t.containerShape ?? "none",
    containerColor: t.containerColor ?? "#e0e7ff",
    containerColorOpacity: t.containerColorOpacity ?? 100,
    containerGradient: t.containerGradient ?? "none",
    containerColor2: t.containerColor2 ?? "#a5b4fc",
    containerGradientAngle: t.containerGradientAngle ?? 135,
    containerPattern: t.containerPattern ?? "none",
    containerPatternColor: t.containerPatternColor ?? "#ffffff",
    containerPatternDensity: t.containerPatternDensity ?? 1,
    iconPattern: t.iconPattern ?? "none",
    iconPatternColor: t.iconPatternColor ?? "#ffffff",
    iconPatternDensity: t.iconPatternDensity ?? 1,
    titleStyle3D: t.titleStyle3D ?? "flat",
    titleDepth3D: t.titleDepth3D ?? 8,
    titleStroke3D: t.titleStroke3D ?? 0,
    titleSkew: t.titleSkew ?? 0,
    sloganStyle3D: t.sloganStyle3D ?? "flat",
    sloganDepth3D: t.sloganDepth3D ?? 4,
    sloganStroke3D: t.sloganStroke3D ?? 0,
    sloganSkew: t.sloganSkew ?? 0,
    titleSloganGap: t.titleSloganGap ?? 12,
    sloganOffsetX: t.sloganOffsetX ?? 0,
    iconTextGap: t.iconTextGap ?? 24,
    titleOffsetX: t.titleOffsetX ?? 0,
    titleOffsetY: t.titleOffsetY ?? 0,
  });
}

// ===== History helpers (defined outside the store factory for clarity) =====
function snapshotOf(s: LogoState): StyleSnapshot {
  return {
    title: s.title, slogan: s.slogan, iconId: s.iconId, qrText: s.qrText,
    titleFont: s.titleFont, sloganFont: s.sloganFont,
    titleColor: s.titleColor, sloganColor: s.sloganColor,
    iconColor: s.iconColor, iconAccent: s.iconAccent,
    background: s.background, layout: s.layout,
    titleSize: s.titleSize, sloganSize: s.sloganSize, iconScale: s.iconScale,
  };
}
function applySnapshot(set: (patch: Partial<LogoState>) => void, snap: StyleSnapshot) {
  set({
    title: snap.title, slogan: snap.slogan, iconId: snap.iconId, qrText: snap.qrText,
    titleFont: snap.titleFont, sloganFont: snap.sloganFont,
    titleColor: snap.titleColor, sloganColor: snap.sloganColor,
    iconColor: snap.iconColor, background: snap.background, layout: snap.layout,
    titleSize: snap.titleSize, sloganSize: snap.sloganSize, iconScale: snap.iconScale,
  });
}
